/* eslint-disable no-magic-numbers */
import { VariableInfo } from "../api/model/variables/Variables";
import BasicRecord from "../utils/BasicRecord";
import generateName from "../utils/generateName";
import { keysOf } from "../utils/object";
import { PartialOnly } from "../utils/UtilityTypes";
import { PLATFORM } from "../enums";

import { CSS_NAMED_COLORS, colorNames } from "./colorData";
import ColorDefinition from "./ColorDefinition";
import ColorFormat from "./ColorFormat";
import Model from "./model";
import NamingConvention from "./NamingConvention";

const projectNamingConventions: BasicRecord<NamingConvention> = Object.freeze({
    [PLATFORM.ANDROID]: NamingConvention.SnakeCase,
    [PLATFORM.IOS]: NamingConvention.CamelCase,
    [PLATFORM.MACOS]: NamingConvention.CamelCase,
    [PLATFORM.WEB]: NamingConvention.KebabCase,
    [PLATFORM.BASE]: NamingConvention.KebabCase
});

const alphaFormatter = new Intl.NumberFormat("en-US", {
    useGrouping: false,
    maximumFractionDigits: 2
});

/**
 * Yes, this is beyond any logical explanation, but looks like this is the best formula
 * we could invent that generates hex colors like Sketch does.
 * The thing here is Sketch treats the color components in a weird way that for each 1/10 interval
 * of [0-255], the point of where second digit of hex representation increases by 1, moves closer
 * to the nearest integer that is less than the number.
 */
function normalizeComponent(channel: number) {
    const normalized = Math.trunc(channel + channel / 255);
    return Math.max(0, Math.min(normalized, 255));
}

function normalizeAlpha(alpha: number) {
    return Math.max(0, Math.min(alpha, 1));
}

function singleComponentBlend(c1: number, a1: number, c2: number, a2: number) {
    if (!a1 && !a2) {
        return 0;
    }

    return (c1 * a1 + c2 * a2 * (1 - a1)) / (a1 + a2 * (1 - a1));
}

/* eslint-disable no-param-reassign */
function hue2rgb(p: number, q: number, t: number) {
    if (t < 0) {
        t += 1;
    }

    if (t > 1) {
        t -= 1;
    }

    if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
    }

    if (t < 1 / 2) {
        return q;
    }

    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }

    return p;
}
/* eslint-enable no-param-reassign */

function formatForPlatform(platform: PLATFORM): ColorFormat {
    switch (platform) {
        case PLATFORM.BASE:
        case PLATFORM.IOS:
        case PLATFORM.MACOS:
            return ColorFormat.RGBA;
        case PLATFORM.ANDROID:
            return ColorFormat.HexARGB;
        case PLATFORM.WEB:
            return ColorFormat.HexRGBA;
        default:
            console.warn(`Color format not defined for platform “${platform}”`);

            return ColorFormat.RGBA;
    }
}

export default class Color extends Model {
    id?: string;
    sourceId?: string;
    name?: string;
    r!: number;
    g!: number;
    b!: number;
    a!: number;
    variableInfo?: VariableInfo;
    requestId?: string;
    index?: number;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    constructor(colorData: PartialOnly<ColorDefinition, "a">) {
        if (colorData instanceof Color) {
            return colorData;
        }

        super();

        const {
            _id, id, name, sourceId, variableInfo,
            r, g, b, a = 1,
            requestId, index
        } = colorData;

        this.id = _id || id;
        this.sourceId = sourceId;

        this.r = normalizeComponent(r);
        this.g = normalizeComponent(g);
        this.b = normalizeComponent(b);
        this.a = normalizeAlpha(a);
        this.name = name;
        this.variableInfo = variableInfo;
        this.requestId = requestId;
        this.index = index;
    }

    static get NAMED_COLORS() {
        return CSS_NAMED_COLORS;
    }

    static fromHex(hex: string) {
        let hexBase = hex.replace("#", "");

        if (hexBase.length === 3 || hexBase.length === 4) {
            hexBase = hexBase.replace(/(.)/g, "$1$1");
        }

        const num = parseInt(hexBase, 16);

        let shiftAmount = (hexBase.length / 2 - 1) * 8;

        const r = (num >> shiftAmount) & 255;
        shiftAmount -= 8;

        const g = (num >> shiftAmount) & 255;
        shiftAmount -= 8;

        const b = (num >> shiftAmount) & 255;
        shiftAmount -= 8;

        const a = shiftAmount < 0 ? 1 : ((num >> shiftAmount) & 255) / 255;

        return new Color({ r, g, b, a });
    }

    static fromHSLA(h: number, s: number, l: number, a = 1) {
        let r;
        let g;
        let b;

        if (s === 0) {
            // Achromatic
            r = l;
            g = l;
            b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;

            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        r *= 255;
        g *= 255;
        b *= 255;

        return new Color({ r, g, b, a });
    }

    formatName(platform: PLATFORM) {
        return generateName(this.name!, projectNamingConventions[platform]);
    }

    override toObject(): ColorDefinition {
        return {
            r: this.r,
            g: this.g,
            b: this.b,
            a: this.a,
            sourceId: this.sourceId,
            name: this.name
        };
    }

    equals(c: ColorDefinition) {
        const other = new Color(c);

        if (!!this.sourceId && !!other.sourceId) {
            return this.sourceId === other.sourceId;
        }

        return this.r === other.r &&
               this.g === other.g &&
               this.b === other.b &&
               alphaFormatter.format(this.a) === alphaFormatter.format(other.a);
    }

    blend(c: ColorDefinition) {
        const other = new Color(c);

        return new Color({
            r: singleComponentBlend(this.r, this.a, other.r, other.a),
            g: singleComponentBlend(this.g, this.a, other.g, other.a),
            b: singleComponentBlend(this.b, this.a, other.b, other.a),
            a: this.a + other.a * (1 - this.a)
        });
    }

    static blendAll(colors: ColorDefinition[]) {
        let blendedColor = new Color(Color.NAMED_COLORS.transparent);

        for (const color of colors) {
            if (!color.a) {
                continue;
            }

            blendedColor = blendedColor.blend(color);

            if (blendedColor.a === 1) {
                break;
            }
        }

        return blendedColor;
    }

    static componentToHex(num: number) {
        const hexNum = Math.max(0, Math.min(num, 255));
        return (hexNum < 16 ? "0" : "") + hexNum.toString(16);
    }

    hexBase() {
        return Color.componentToHex(this.r) +
               Color.componentToHex(this.g) +
               Color.componentToHex(this.b);
    }

    toHexString(options: { alphaAsPrefix?: boolean; } = { alphaAsPrefix: false }) {
        let hexCode = this.hexBase();

        if (this.a < 1) {
            const alpha = this.a * 255;
            const hexA = Color.componentToHex(Math.trunc(alpha + (alpha / 255)));

            hexCode = options.alphaAsPrefix ? (hexA + hexCode) : (hexCode + hexA);
        }

        return `#${hexCode}`;
    }

    toRGBABase() {
        const rgb = `${this.r} ${this.g} ${this.b}`;

        return this.a < 1
            ? `rgba ${rgb}, ${alphaFormatter.format(this.a)}`
            : `rgb ${rgb}`;
    }

    toRGBString() {
        return `rgb ${this.r} ${this.g} ${this.b}`;
    }

    toRGBAString() {
        const rgb = `${this.r}, ${this.g}, ${this.b}`;

        return this.a < 1
            ? `rgba(${rgb}, ${alphaFormatter.format(this.a)})`
            : `rgb(${rgb})`;
    }

    #toHSL() {
        const r = this.r / 255;
        const g = this.g / 255;
        const b = this.b / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);

        const l = (max + min) / 2;
        let h!: number;
        let s;

        if (max === min) {
            // Achromatic
            h = 0;
            s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;

                case g:
                    h = (b - r) / d + 2;
                    break;

                case b:
                    h = (r - g) / d + 4;
                    break;

                default:
                    // unreachable!();
            }

            h /= 6;
        }

        return { h, s, l };
    }

    toHSLString() {
        const hslColor = this.#toHSL();
        const hsl = `${Math.round(hslColor.h * 360)}, ${Math.round(hslColor.s * 100)}%, ${Math.round(hslColor.l * 100)}%`;

        return `hsl ${hsl}`;
    }

    toHSLAString() {
        const hslColor = this.#toHSL();
        const hsl = `${Math.round(hslColor.h * 360)}, ${Math.round(hslColor.s * 100)}%, ${Math.round(hslColor.l * 100)}%`;

        return this.a < 1
            ? `hsla(${hsl}, ${alphaFormatter.format(this.a)})`
            : `hsl(${hsl})`;
    }

    toFormattedString({
        colorFormat,
        platform
    } : {
        colorFormat?: ColorFormat;
        platform: PLATFORM;
    }) {
        const calculatedColorFormat = colorFormat ?? formatForPlatform(platform);

        switch (calculatedColorFormat) {
            case ColorFormat.RGBA:
                return this.toRGBString();
            case ColorFormat.HexRGBA:
                return this.toHexString();
            case ColorFormat.HexARGB:
                return this.toHexString({ alphaAsPrefix: true });
            case ColorFormat.HSL:
                return this.toHSLString();
            default:
                throw new Error(`Converting color to formatted string failed with unknown format “${calculatedColorFormat}”`);
        }
    }

    toCSS(colorFormat?: string) {
        switch (colorFormat) {
            case "hex":
                return this.toHexString();

            case "rgb":
                return this.toRGBAString();

            case "hsl":
                return this.toHSLAString();

            case "default":
            default:
                return this.a < 1 ? this.toRGBAString() : this.toHexString();
        }
    }

    generateName(platform: PLATFORM) {
        let ndf = 0;
        let cl = -1;
        let df = -1;

        for (let i = 0; i < colorNames.length; i++) {
            const c = new Color(colorNames[i]);

            if (this.r === c.r && this.g === c.g && this.b === c.b) {
                return generateName(c.name!, projectNamingConventions[platform]);
            }

            const hsl = this.#toHSL();
            keysOf(hsl).forEach(comp => (hsl[comp] *= 255));

            const cHsl = c.#toHSL();
            keysOf(cHsl).forEach(comp => (cHsl[comp] *= 255));

            ndf = Math.pow(this.r - c.r, 2) + Math.pow(this.g - c.g, 2) + Math.pow(this.b - c.b, 2) +
                  (Math.pow(hsl.h - cHsl.h, 2) + Math.pow(hsl.s - cHsl.s, 2) + Math.pow(hsl.l - cHsl.l, 2)) * 2;

            if (df < 0 || ndf < df) {
                df = ndf;
                cl = i;
            }
        }

        return generateName(colorNames[cl].name, projectNamingConventions[platform]);
    }
}
